import { BEIGE_700, GRAY_200, GRAY_800, GRAY_900, MOBILE_VIEW_HEIGHT_QUERY, MOBILE_VIEW_WIDTH_QUERY, TABLET_VIEW_QUERY } from 'constants/styling/theme'
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import React, { Fragment, KeyboardEvent, ReactNode, useCallback } from 'react'

import { APIRequestErrorType } from 'constants/API'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { Close } from '@mui/icons-material'
import { Color } from 'constants/assets'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import ErrorMessage from 'components/common/ErrorMessage/ErrorMessage'
import { ExpandedVisual } from './ExpandedVisual.component'
import { KeyboardEventKey } from 'constants/misc'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import Modal from '../Modals/Modal/Modal'
import { Nullable } from 'models/helpers'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { VisualClientDTO } from 'models/visuals'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
import styles from './ExpandedVisualPopup.module.sass'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

export enum ExpandedVisualSwitchDirection {
  LEFT,
  RIGHT
}

/**
 * @interface Props Input properties
 */
interface Props {
  /** Whether the popup is open */
  isOpen: boolean
  /** Signed Url for displaying image in popup */
  signedUrl?: string
  /** File name of image */
  filename?: string
  /** Label of image title badge */
  label?: string
  /** OnClick action to switch to previous/next visual */
  switchExpandedVisual: (direction: ExpandedVisualSwitchDirection) => void
  /** OnClick action to close the popup */
  onClose: () => void
  /** Metadata of a visual */
  visualMetadata?: Nullable<VisualClientDTO>
  /** Slot for footer buttons - already in Stack*/
  footerActionsSlot?: ReactNode
  /** Whether loading should be displayed */
  isLoading?: boolean
}

/**
 * @component ExpandedVisualPopup 
 * @example
 * <ExpandedVisualPopupBase isOpen={true} signedUrl={url} filename="photo_01" switchExpandedVisual={() => {}} onClose={()=> {}} />
 */

export const ExpandedVisualPopupBase: React.FC<Props> = ({
  isOpen,
  signedUrl,
  filename,
  label,
  switchExpandedVisual,
  onClose,
  visualMetadata,
  footerActionsSlot,
  isLoading = false,
}) => {

  const isMobileViewWidth = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const isMobileViewHeight = useMediaQuery(MOBILE_VIEW_HEIGHT_QUERY)
  const isTabletView = useMediaQuery(TABLET_VIEW_QUERY)
  const { t } = useTranslation(['common', 'actions'])

  const keyDownHandler = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case KeyboardEventKey.ESCAPE:
        return onClose()
      case KeyboardEventKey.ARROW_LEFT:
        return switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)
      case KeyboardEventKey.ARROW_RIGHT:
        return switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)
    }
  }, [onClose, switchExpandedVisual])

  let style: React.CSSProperties = {}
  if (signedUrl) {
    style.backgroundImage = `url('${signedUrl}')`
  }

  return (
    <Fragment>
      {(isMobileViewWidth && isMobileViewHeight) || isTabletView ?
        <ResponsiveDrawer
          title={filename || ''}
          isOpen={isOpen}
          onClose={onClose}
          height="70vh"
        >

          <ExpandedVisual
            signedUrl={signedUrl}
            visualMetadata={visualMetadata}
          />

          <Stack flexDirection="row" justifyContent="center" alignItems="center" gap=".8rem" marginTop="2.4rem">
            <MUIButton
              type="secondaryBorder"
              variant="outlined"
              size="lg"
              fullWidth
              startIcon={<ArrowBackRoundedIcon fontSize="large" />}
              onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)}
            >
              {t('Previous')}
            </MUIButton>
            <MUIButton
              type="secondaryBorder"
              variant="outlined"
              size="lg"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon fontSize="large" />}
              onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)}
            >
              {t('Next')}
            </MUIButton>
          </Stack>
        </ResponsiveDrawer>
        :
        <Modal
          modalContentClassName={styles.expandedVisualPopup}
          isOpen={isOpen}
          onClickOutside={onClose}
          onKeyDown={keyDownHandler}
          footerContent={
            <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" gap=".8rem" padding="1.2rem 1.6rem">
              {footerActionsSlot}
            </Stack>
          }
        >

          {isLoading &&
            <CircularProgress
              variant="indeterminate"
              size={40}
              thickness={5}
              sx={{
                position: 'absolute',
                top: 'calc(50% - 20px)',
                left: 'calc(50% - 20px)',
                color: Color.GRAY_TEXT,
                borderRadius: '50%',
                zIndex: '10',
                // Background ring for unfilled progress
                boxShadow: `inset 0 0 0px 3px ${BEIGE_700}`
              }}
            />
          }

          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            padding="1.6rem 2.4rem"
          >
            <Stack gap=".8rem" flexDirection="row" alignItems="center">
              <Typography variant="text-xl" fontWeight="semiBold" color={GRAY_900}>{filename}</Typography>
              {!!label && <MUIBadge label={label} size="sm" color="gray" />}
            </Stack>
            <IconButton
              size="small"
              sx={{
                color: GRAY_800,
                border: `1px solid ${GRAY_200}`,
                padding: '.6rem'
              }}
              onClick={onClose}
            >
              <Close fontSize="large" />
            </IconButton>
          </Stack>

          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap="2.4rem"
            padding="4.8rem 2.4rem"
            sx={{
              height: 'calc(100% - 7.3rem)',
              boxSizing: 'border-box',
              opacity: isLoading ? '50%' : '100%',
            }}
          >
            <Stack height="100%" flexDirection="row" alignItems="center">
              <IconButton
                size="small"
                sx={{
                  color: GRAY_800,
                  border: `1px solid ${GRAY_200}`,
                  padding: '.8rem'
                }}
                onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)}
              >
                <WestRoundedIcon fontSize="large" />
              </IconButton>
            </Stack>

            <ExpandedVisual
              signedUrl={signedUrl}
              visualMetadata={visualMetadata}
            />

            {!filename &&
              <ErrorMessage className={styles.error} error_type={APIRequestErrorType.NOTFOUND_ERROR} />
            }

            <Stack height="100%" flexDirection="row" alignItems="center">
              <IconButton
                size="small"
                sx={{
                  color: GRAY_800,
                  border: `1px solid ${GRAY_200}`,
                  padding: '.8rem'
                }}
                onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)}
              >
                <EastRoundedIcon fontSize="large" />
              </IconButton>
            </Stack>
          </Stack>
        </Modal>
      }
    </Fragment>
  )
}
